import { useState } from "react";

import { useClearNpcPrice, useSetNpcPrice } from "../api/queries";
import type { NpcPrice } from "../api/types";
import { adena, when } from "../format";
import { ErrorBand } from "./ErrorBand";
import { Panel } from "./Panel";
import { TitleBand } from "./TitleBand";

/** What an NPC store charges, for an uploader or an owner to record.
 *
 *  A vendor price is the one number in this app that nobody observed — it is
 *  typed by a person and then feeds every crafting cost the item appears in.
 *  So the panel says who set it and when, and the recipe tree marks a material
 *  priced this way: a reader must always be able to tell a scanned figure from
 *  an asserted one.
 *
 *  It applies only where the market is silent. A single scanned ask outranks
 *  it, which is why the hint says "when nothing is scanned" rather than
 *  promising the number will be used. */
export function NpcPricePanel({ itemId, price }: { itemId: number; price: NpcPrice | null }) {
  const [draft, setDraft] = useState("");
  const set = useSetNpcPrice(itemId);
  const clear = useClearNpcPrice(itemId);

  const current = price?.price ?? null;
  const parsed = Number(draft);
  // Not Number.isNaN: Number("") is 0 and Number("1.5") is 1.5, and a vendor
  // charges whole adena. The same guard shape the item page uses for its id.
  const valid = Number.isInteger(parsed) && parsed > 0;

  return (
    <>
      <TitleBand title="NPC price" note="used when nothing is scanned" />
      <Panel>
        <p className="scan-meta">
          <span>
            {current === null ? (
              "No vendor price recorded."
            ) : (
              <>
                {/* No colour class: `.gold` exists only as `.cost-summary
                    .gold`, so a bare span would take none. The figure reads as
                    prose here, and tokens.css owns whether that ever changes. */}
                <span>{adena(current)}</span>
                {price?.set_by ? ` · set by ${price.set_by}` : ""}
                {price?.set_at ? ` · ${when(price.set_at)}` : ""}
              </>
            )}
          </span>
        </p>
        <div className="strip">
          <input
            type="number"
            min="1"
            step="1"
            value={draft}
            placeholder="price in adena"
            aria-label="NPC price in adena"
            onChange={(event) => setDraft(event.target.value)}
          />
          <button
            type="button"
            disabled={!valid || set.isPending}
            onClick={() => set.mutate(parsed, { onSuccess: () => setDraft("") })}
          >
            {current === null ? "Set" : "Update"}
          </button>
          {current === null ? null : (
            <button type="button" disabled={clear.isPending} onClick={() => clear.mutate()}>
              Clear
            </button>
          )}
        </div>
        {set.isError || clear.isError ? (
          <ErrorBand
            message="Could not save the price."
            hint="It may have been changed elsewhere. Reload and try again."
          />
        ) : null}
      </Panel>
    </>
  );
}
